import React, { FC } from 'react';
import styled from 'styled-components';
import { connect, useDispatch } from 'react-redux';
import { Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import { useOverlayUrlService } from '../useOverlay';
import ControlsArea from '../ControllsArea';
import ScrollableArea from '../ScrollableArea';
import Header, { TitlePosition } from './Header';
import { hideOverlay } from 'Client/redux/header/overlays/actions';
import StateInterface from 'Client/redux/types';
import { getOverlayById } from 'Client/redux/header/overlays/selectors';
import ErrorBoundary from 'Client/components/Generic/ErrorBoundaries';
import GenericErrorRetry from 'Client/components/Generic/ErrorPages/GenericErrorRetry';
import LineSeparator from 'Client/components/Generic/LineSeparator';

const StyledOverlay = styled.section`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.white};
    z-index: ${({ theme }) => theme.zindexCategoryNav};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;
interface FlyoutProps {
    children: React.ReactElement | React.ReactElement[];
    identifier: string;
    show?: boolean;
    title?: string;
    cleanup?: () => void;
    hasCustomHeader?: boolean;
    closeBtnPosition?: Direction.Right | Direction.Left;
    titlePosition?: TitlePosition.CENTER | TitlePosition.LEFT;
    showHeaderBorder?: boolean;
    denseHeader?: boolean;
}

// Flyout component can have either one or two children.
// If there are two children, the first one is put in main content area
// and the second one - in a control area:
// |header|
// | main |
// | main |
// | main |
// |control|
const Flyout: FC<FlyoutProps> = ({
    children = null,
    show = false,
    identifier,
    title,
    cleanup = () => {},
    hasCustomHeader = false,
    closeBtnPosition,
    titlePosition = TitlePosition.CENTER,
    showHeaderBorder = false,
    denseHeader = false,
}) => {
    const { pullOffOverlay, hasAnActiveOverlay } = useOverlayUrlService();
    const dispatch = useDispatch();
    if (!show) {
        return null;
    }

    const handleDismissClick = (): void => {
        if (hasAnActiveOverlay) {
            pullOffOverlay();
        } else {
            dispatch(hideOverlay(identifier));
        }
        cleanup();
    };

    let mainContent = children;
    let controlAreaContent = null;

    if (Array.isArray(children)) {
        [mainContent, controlAreaContent] = children;
    }

    const idLowerCase = identifier.toLocaleLowerCase();

    return (
        <>
            <ErrorBoundary boundaryName={`Flyout-${idLowerCase}`} fallbackComponent={<GenericErrorRetry />}>
                <StyledOverlay id={idLowerCase} data-testid={idLowerCase}>
                    {!hasCustomHeader && (
                        <Header
                            showBorder={showHeaderBorder}
                            dense={denseHeader}
                            onCloseClick={handleDismissClick}
                            titlePosition={titlePosition}
                            title={title}
                            closeBtnPosition={closeBtnPosition}
                        />
                    )}
                    <ScrollableArea>{mainContent}</ScrollableArea>
                    {controlAreaContent && (
                        <ControlsArea>
                            <LineSeparator />
                            {controlAreaContent}
                        </ControlsArea>
                    )}
                </StyledOverlay>
            </ErrorBoundary>
        </>
    );
};

const mapStateToProps = (state: StateInterface, ownProps: FlyoutProps) => {
    const overlay = getOverlayById(state.header.overlays, ownProps.identifier);

    return { show: overlay.isActive, identifier: ownProps.identifier };
};

export * from './Header';
export default connect(mapStateToProps)(Flyout);
